import { cn } from '@invent/wl-ui-kit-next';
import { Alert, AlertDescription, AlertTitle, Button } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';

import { PageHeader, StatusChip } from '@/design';
import { ApiError, ProblemType, type IEtagged } from '@/domains/api';
import { useCan } from '@/domains/auth';
import { formatDateTime } from '@/domains/survey';
import type { IInternalSchemaField } from '../../api/internal-field-contracts';
import { internalFieldsApi } from '../../api/internal-fields-api';
import type { IAdminDisclosure, IAdminDisclosureChange } from '../../api/participant-contracts';
import type {
  IWorkItemAssignee,
  IWorkItemDetail,
  IWorkItemEvent,
  IWorkItemFirmChange,
  IWorkItemFirmSide,
  IWorkItemRow,
  IWorkItemSibling
} from '../../api/work-item-contracts';
import { workItemsApi } from '../../api/work-items-api';
import { LONG_TEXT_WHOLE } from '../lists/cells';
import { WORK_ITEMS_MAX_PAGE } from '../lists/work-page/pager';
import { LoadError, useAdminData } from '../../components/admin-page';
import { formatDay } from '../../components/days';
import { ChangedFields, DisclosureCard } from '../../components/disclosure-card';
import { useListReread } from '../../components/list-area';
import { CardLink, personHubPath } from '../../components/participant-link';
import { disclosureAnchor, type IRevealState } from '../person-hub/disclosures';
import { NoteLine, sentence, unanswered, type ISaidNote } from '../person-hub/said';
import { changeKindWords } from '../../disclosure-fields';
import { refreshRunPath, workItemPath } from '../../paths';
import { refusalMessage } from '../../refusal';
import {
  actorRoleLabel,
  decisionLabel,
  eventLabel,
  isKnownEvent,
  lookupLabel,
  memberParts,
  originLabel,
  transitionRefusal,
  triggerLabel
} from '../../work-items';
import {
  AssigneeControl,
  controlRefusal,
  DecisionButtons,
  FollowUpControl,
  ReopenButton,
  type IWorkItemWrites
} from './work-item-writes';
import { DraftMark, U4_EXPLANATION, U4_SURVEYS } from './answer-columns';
import { lastLevelOf, useEscalationLevels } from './escalation';
import { removeWrites, StaffValue, staffColumnFields } from './staff-columns';
import { WorkItemStateChip } from './work-items-table';

/**
 * One work item, in the queue's record pane (SR-222, F9's R6) — `/team/compliance/:workItemId`,
 * or `/team/tasks/:workItemId` from My tasks, the list beside it: what raised it, the moves its
 * state takes, and its history.
 *
 * **Three arms over one read** (`getWorkItem`): a line of text while it is out — never the kit's
 * skeleton, the split pane's rule — survey-api's refusal said, or the item. A 404 is its own
 * sentence, never a white screen: an id in the address that names no item (a removed one, a
 * mistyped link, the demo's old `/team/compliance/fa`) is answered like a missing record. A
 * later read that fails over a drawn item keeps the item, under "Could not refresh".
 *
 * **The `ETag` is kept beside the item** (R4), as read, and replaced by every write's answer
 * before the item is read again — so a write between the answer and the re-read still quotes
 * the newest revision. A 409 `revision-conflict` says the item changed since it was opened, with
 * Reload: the events, newest first, say what changed, since the wire does not name who. A 409
 * `invalid-transition` is said in survey-api's words, and the item read again. An answer that
 * never arrived reads the item again and asks to be checked (the hub's rule).
 *
 * **Every write that lands re-reads the item and the list beside it** (`useListReread`): a state
 * or an assignee changed here is a row that moved there.
 *
 * Everything drawn is as sent: the criteria by name and version (CMP-08), a change's `changed` as
 * stored, the lookup's status and reason under the registry that answered (`lookupSource`, the
 * owner's Q3), a Salesforce task's id as selectable text (no link into Salesforce: no base URL is
 * configured, and it is being switched off).
 *
 * **The disclosure is reviewed here, not on the hub** (SR-396 W1, AC5): the detail carries it, and
 * the pane draws it open with the hub's own card (`DisclosureCard`), the change's kind beside it
 * and "What changed" under it — the fields that change moved marked on the card — for every kind
 * of change, a `created` one included. Nothing re-reads the hub's disclosures route for it. The
 * hub is still a link away, secondary, carrying the disclosure's anchor in its history state — the
 * hub reveals it (F9's Task 6); the anchor is per member, and the item's participant is the member.
 * A detail naming a disclosure it does not carry (an API before SR-396, or a disclosure gone from
 * the store) keeps that link alone, as before. "What raised this" says the disclosure itself — its
 * type, day, allegations and resolution (SR-410) — rather than a link to it.
 *
 * **A CRD or firm change says what moved** (`firmChange`, SR-410): the firms before and after, the
 * day of the move, and the CRDs where they differ.
 *
 * **A mark carried from Salesforce's "RJ reviewed U4"** (`U4_REVIEWED_KEY`) is migrated history
 * survey-api refuses to change: it draws no Decision section.
 *
 * **The title is the item's own address**, a link a reader can open in a new tab.
 *
 * **The other items the same change raised** (W4 — one per queue, the owner kept them apart) are
 * listed, each opening on its own queue: none is decided here, since a Remove writes its own
 * survey's rating. An absent list (an older API) draws nothing, never "no other item".
 *
 * **Eligibility** is the participant's Quality rating and Travel on the item's survey — what a
 * Remove writes (F9's Task 8) — read as the queue reads them, the participant's rows on that
 * survey asked for the two fields (any row carries the same values: survey-api reads them off the
 * participant's newest response), and read again with the item after every write.
 *
 * **U4 explanation** is the flagged person's own account of the disclosure from the survey (WF-05
 * step 2, SR-398) — the queue's `u4.explanation`, in the same read, from this item's own row: on a
 * team or a firm it is the flagged member's. Drawn whole, "draft" where the survey is not submitted.
 */
export function AdminWorkItemPage({
  crumb = 'Compliance queues'
}: {
  /** The list the item is read from — the queue's, or My tasks'. */
  crumb?: string;
}) {
  const { workItemId = '' } = useParams();
  // Keyed by the id: the route's element is not remounted when the list beside it opens another
  // item, and a pane kept across items would show one under the other's address (the hub's
  // lesson).
  return <WorkItemPane key={workItemId} workItemId={workItemId} crumb={crumb} />;
}

/** The read's answer — `found` null for a 404. */
interface IRead {
  found: Nullable<IEtagged<IWorkItemDetail>>;
}

function WorkItemPane({ workItemId, crumb }: { workItemId: string; crumb: string }) {
  const location = useLocation();
  const rereadList = useListReread();
  const { data, error, reload } = useAdminData<IRead>(
    async (signal) => ({ found: await workItemsApi.getWorkItem(workItemId, signal) }),
    [workItemId]
  );
  const assignees = useAdminData<IWorkItemAssignee[]>(
    (signal) => workItemsApi.workItemAssignees(signal),
    []
  );

  /** A write's answer, drawn until the read after it lands. */
  const [adopted, setAdopted] = React.useState<Nullable<IEtagged<IWorkItemRow>>>(null);
  React.useEffect(() => setAdopted(null), [data]);
  const [notice, setNotice] = React.useState<Nullable<ISaidNote>>(null);
  const [conflict, setConflict] = React.useState(false);

  const readAgain = React.useCallback(async () => {
    rereadList();
    return reload();
  }, [reload, rereadList]);

  if (data === null) {
    return (
      <>
        <PageHeader title="Work item" crumb={crumb} />
        <div className="grid gap-6 px-6 py-6">
          {error ? (
            <LoadError message={error} />
          ) : (
            <p className="text-sm text-muted-foreground">Reading the item…</p>
          )}
        </div>
      </>
    );
  }

  if (data.found === null) {
    return (
      <>
        <PageHeader title="Work item" crumb={crumb} />
        <div className="grid gap-6 px-6 py-6">
          <Alert>
            <AlertTitle>No such item</AlertTitle>
            <AlertDescription>
              No work item has this address — it may have been removed from the page you came
              from.
            </AlertDescription>
          </Alert>
        </div>
      </>
    );
  }

  const detail = data.found.body;
  const row = adopted?.body ?? detail.row;
  const migrated = detail.sfTaskId?.startsWith(U4_REVIEWED_KEY) ?? false;
  const etag = adopted?.etag ?? data.found.etag;

  /** A write landed: its answer drawn at once, then the item and the list read again. */
  const onLanded = (answer?: IEtagged<IWorkItemRow>) => {
    setNotice(null);
    setConflict(false);
    if (answer) setAdopted(answer);
    void readAgain().then((read) => {
      // A decision's answer carries no `ETag` to adopt: until a read lands, the pane draws the
      // item as it was, and an assign or a reopen would quote a revision the decision spent.
      if (!read && !answer) {
        setNotice({
          tone: 'refused',
          title: 'Decided — but not read again',
          text:
            'The decision landed, but the item could not be read again, so it is drawn as it ' +
            'was. Read the page again before changing anything else on it.'
        });
      }
    });
  };

  /** A write failed: what says the item moved under the page is the pane's to say; the rest is
   *  the control's. */
  const onTrouble = (failure: unknown): Nullable<string> => {
    if (failure instanceof ApiError && failure.isProblem(ProblemType.revisionConflict)) {
      setConflict(true);
      return null;
    }
    /** Said at the top of the pane, with the item read again. */
    const sayAndReread = (title: string, text: string) => {
      setNotice({ tone: 'refused', title, text });
      void readAgain();
      return null;
    };
    const refused = transitionRefusal(failure);
    if (refused) {
      return sayAndReread(
        'Not done',
        `${sentence(refused.reason ?? 'That move was refused for the item’s state')} ` +
          'The item has been read again.'
      );
    }
    if (failure instanceof ApiError && failure.status === 404) {
      return sayAndReread(
        'Not done',
        'There is no such item any more — it has been read again.'
      );
    }
    if (unanswered(failure)) {
      return sayAndReread(
        'No answer',
        'The answer did not arrive — the item has been read again: check whether it changed ' +
          'before trying again.'
      );
    }
    return controlRefusal(failure);
  };

  const writes: IWorkItemWrites = {
    row,
    etag,
    assignees: assignees.error ? null : assignees.data,
    assigneesError: assignees.error,
    onLanded,
    onTrouble
  };

  return (
    <>
      <PageHeader
        title={
          <Link
            // The list beside keeps its tab, view and filters: they ride in the link's state.
            to={workItemPath(row.id)}
            state={location.state}
            data-work-item-self=""
            className="underline-offset-4 hover:underline"
          >
            {row.subject}
          </Link>
        }
        documentTitle={row.subject}
        crumb={crumb}
        meta={
          <>
            <WorkItemStateChip state={row.state} />
            {row.state === 'reviewed' && row.decision && (
              <StatusChip>{decisionLabel(row.decision)}</StatusChip>
            )}
            <StatusChip>{row.surveyCode}</StatusChip>
            <StatusChip>{triggerLabel(row.trigger)}</StatusChip>
          </>
        }
      />
      <div className="grid gap-6 px-6 py-6">
        {error && (
          <LoadError message={`Could not refresh — showing the item as last read. ${error}`} />
        )}
        {conflict && (
          <Alert variant="destructive">
            <AlertTitle>This item changed since you opened it</AlertTitle>
            <AlertDescription>
              <span>
                Someone else changed it first; nothing was saved. Its history below says what
                changed once it is read again.
              </span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="mt-2"
                onClick={() =>
                  void readAgain().then((read) => {
                    if (read) setConflict(false);
                  })
                }
              >
                Reload
              </Button>
            </AlertDescription>
          </Alert>
        )}
        {notice && <NoteLine note={notice} />}

        <Facts row={row} />
        <RaisedBy detail={detail} row={row} />
        <DisclosureSection detail={detail} row={row} />
        <Siblings siblings={detail.siblings} />
        <Beside row={detail.row} read={data} />

        {!migrated && (
          <PaneSection title="Decision">
            {etag === null && (
              <p className="text-sm text-muted-foreground">
                This item came without its revision, so it cannot be assigned, followed up or
                reopened here — reload the page.
              </p>
            )}
            <DecisionButtons {...writes} />
            <ReopenButton {...writes} />
            <AssigneeControl {...writes} />
            <FollowUpControl {...writes} />
          </PaneSection>
        )}

        <History events={detail.events} />
      </div>
    </>
  );
}

interface IBeside {
  /** The two Remove writes the item's survey has, and the participant's values of them. */
  fields: IInternalSchemaField[];
  values: Record<string, string>;
  /** This item's U4 explanation, on a survey that asks for one; null on any other. */
  explanation: Nullable<{ value: unknown; draft: boolean }>;
}

/** What the pane reads of the participant beside the item — see the module's comment: the queue's
 *  rows of the participant on the item's survey, asked for the Remove writes and the U4 explanation,
 *  this item's row first. */
function useBeside(row: IWorkItemRow, read: IRead) {
  return useAdminData<IBeside>(
    async (signal) => {
      try {
        const schema = await internalFieldsApi.schema(row.surveyCode);
        const fields = removeWrites(staffColumnFields(schema));
        const explains = U4_SURVEYS.has(row.surveyCode);
        if (fields.length === 0 && !explains) return { fields, values: {}, explanation: null };
        const page = await workItemsApi.listWorkItems(
          {
            survey: [row.surveyCode],
            participantId: row.participant.id,
            fields: [...fields.map((field) => field.code), ...(explains ? [U4_EXPLANATION] : [])],
            pageSize: explains ? WORK_ITEMS_MAX_PAGE : 1
          },
          signal
        );
        // The staff values are the participant's, any row's; the explanation is this item's own —
        // on a team another row is another member's.
        const own = page.items.find((item) => item.id === row.id);
        return {
          fields,
          values: (own ?? page.items[0])?.internal ?? {},
          explanation:
            explains && own
              ? { value: own.answers?.[U4_EXPLANATION], draft: own.answersStatus === 'draft' }
              : null
        };
      } catch (failure) {
        throw new Error(refusalMessage(failure, 'Could not read the participant’s values'));
      }
    },
    // `read` is the item's read: every write reads the item again, and this with it.
    [row.surveyCode, row.participant.id, row.id, read]
  );
}

/** The U4 explanation and Eligibility, over the one read of the participant beside the item. */
function Beside({ row, read }: { row: IWorkItemRow; read: IRead }) {
  const beside = useBeside(row, read);
  return (
    <>
      <U4Explanation surveyCode={row.surveyCode} beside={beside} />
      <Eligibility beside={beside} />
    </>
  );
}

/** The flagged person's U4 explanation — nothing while it is first read or after it failed (said once,
 *  by Eligibility), nothing on a survey that asks for none; "Not answered" where the survey holds none. */
function U4Explanation({ surveyCode, beside }: { surveyCode: string; beside: ReturnType<typeof useBeside> }) {
  if (!U4_SURVEYS.has(surveyCode)) return null;
  if (beside.data === null) return null;
  const explanation = beside.data?.explanation;
  if (!explanation) return null;
  const text = typeof explanation.value === 'string' ? explanation.value.trim() : '';
  return (
    <PaneSection title="U4 explanation">
      {text === '' ? (
        <p className="text-sm text-muted-foreground">Not answered</p>
      ) : (
        <p data-u4-explanation="" className={cn('text-sm', LONG_TEXT_WHOLE)}>
          {explanation.draft && <DraftMark />}
          {text}
        </p>
      )}
    </PaneSection>
  );
}

/** The two Remove writes as the participant holds them — see the module's comment. Nothing while
 *  the first read is out, and nothing for a survey whose form has neither field; a failure is
 *  said, and a failed re-read keeps the values last read. */
function Eligibility({ beside }: { beside: ReturnType<typeof useBeside> }) {
  const { data, error } = beside;
  if (data === null && !error) return null;
  if (data !== null && data.fields.length === 0) return null;
  return (
    <PaneSection title="Eligibility">
      {error && (
        <LoadError
          message={data === null ? error : `Could not refresh — showing it as last read. ${error}`}
        />
      )}
      {data && (
        <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
          {data.fields.map((field) => (
            <Fact key={field.code} label={field.name}>
              <StaffValue text={data.values[field.code]} field={field} />
            </Fact>
          ))}
        </dl>
      )}
    </PaneSection>
  );
}

/** A titled region of the pane — what a reader and a spec find by name. The refresh run's record
 *  (`refresh/run-pane.tsx`) is drawn with this and the two below, so the two panes read alike. */
export function PaneSection({ title, children }: { title: string; children: React.ReactNode }) {
  const id = React.useId();
  return (
    <section aria-labelledby={id} className="grid gap-3">
      <h2 id={id} className="text-base font-semibold text-[var(--color-royal-blue)]">
        {title}
      </h2>
      {children}
    </section>
  );
}

/** One label over its value — the pane's facts, two columns where there is room. A `data-*`
 *  hook given is the fact's own, on its wrapper. */
export function Fact({
  label,
  wide = false,
  children,
  ...hooks
}: {
  label: string;
  /** Both columns — a long text's. */
  wide?: boolean;
  children: React.ReactNode;
  [hook: `data-${string}`]: string;
}) {
  return (
    <div className={cn('grid content-start gap-0.5', wide && 'sm:col-span-2')} {...hooks}>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm wrap-anywhere">{children}</dd>
    </div>
  );
}

/** A fact with no value. */
export function Missing() {
  return <span className="text-muted-foreground">—</span>;
}

/** Whose item it is and where it stands, as the row says. */
function Facts({ row }: { row: IWorkItemRow }) {
  const last = lastLevelOf(useEscalationLevels());
  return (
    <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
      <Fact label="Advisor or team">
        <CardLink to={personHubPath(row.participant.id)}>{row.participant.displayName}</CardLink>
        {row.participant.firmName && (
          <span className="block text-xs text-muted-foreground">{row.participant.firmName}</span>
        )}
      </Fact>
      <Fact label="About" data-member="">
        <About row={row} />
      </Fact>
      <Fact label="Raised">{formatDateTime(row.raisedAt)}</Fact>
      <Fact label="Due">{row.dueOn ? formatDay(row.dueOn) : <Missing />}</Fact>
      <Fact label="Follow up on">{row.followUpOn ? formatDay(row.followUpOn) : <Missing />}</Fact>
      <Fact label="Pending / final">
        {row.disclosures ? (
          <span className="tabular-nums">
            {row.disclosures.pending} / {row.disclosures.final}
          </span>
        ) : (
          <Missing />
        )}
      </Fact>
      <Fact label="Decided">{row.decidedAt ? formatDateTime(row.decidedAt) : <Missing />}</Fact>
      {row.escalationLevel != null && (
        <Fact label="Escalation level" data-escalation-level={String(row.escalationLevel)}>
          {row.escalationLevel}
          {last ? ` of ${last}` : ''}
        </Fact>
      )}
      <Fact label="Last modified">
        {formatDateTime(row.updatedAt)}
        <span className="block text-xs text-muted-foreground">
          {row.updatedBy?.email ?? 'System'}
        </span>
      </Fact>
    </dl>
  );
}

/** The team member an item is about (SR-396 W3): the name, the roster slot and the CRD, each on
 *  its line where sent — a person's own item draws its CRD alone — and the dash only where none
 *  is. */
function About({ row }: { row: IWorkItemRow }) {
  const [first, ...rest] = memberParts(row);
  if (first === undefined) return <Missing />;
  return (
    <>
      {first}
      {rest.map((part) => (
        <span key={part} className="block text-xs text-muted-foreground">
          {part}
        </span>
      ))}
    </>
  );
}

/** What raised the item: the trigger and origin, the criteria that met it, a lookup's answer, a
 *  Salesforce task, and the BrokerCheck refresh run that raised it (F10's Q3) — linked for
 *  `integrations.configure` alone, since survey-api refuses any other reader the run's route; the
 *  disclosure it is about, said, and a firm change's move (SR-410). A detail without its
 *  disclosure (see the module's comment) also draws here what the Disclosure section would: the
 *  change's diff and the link to the hub. */
function RaisedBy({ detail, row }: { detail: IWorkItemDetail; row: IWorkItemRow }) {
  const can = useCan();
  const carried = Boolean(detail.disclosure);
  const disclosureId = carried ? null : (detail.disclosureId ?? row.disclosureId);
  return (
    <PaneSection title="What raised this">
      <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
        <Fact label="Trigger">
          {triggerLabel(row.trigger)}
          {triggerLabel(row.trigger) !== row.trigger && (
            <span className="ml-1.5 font-mono text-xs text-muted-foreground">{row.trigger}</span>
          )}
        </Fact>
        <Fact label="Raised by">{originLabel(row.origin)}</Fact>
        {row.criteria.length > 0 && (
          <Fact label="Concern criteria">
            <ul className="grid gap-0.5">
              {row.criteria.map((criterion) => (
                <li key={`${criterion.code}@${criterion.version}`} data-criterion={criterion.code}>
                  {criterion.name} · v{criterion.version}
                </li>
              ))}
            </ul>
          </Fact>
        )}
        {(detail.lookupStatus !== null || detail.errorReason !== null) && (
          <Fact label={lookupLabel(detail.lookupSource)}>
            {detail.lookupStatus ?? <Missing />}
            {detail.errorReason && (
              <span className="block text-xs text-muted-foreground">{detail.errorReason}</span>
            )}
          </Fact>
        )}
        {detail.sfTaskId &&
          (detail.sfTaskId.startsWith(U4_REVIEWED_KEY) ? (
            <Fact label="Origin">Migrated from RJ reviewed U4</Fact>
          ) : (
            <Fact label="Salesforce task">
              <span className="font-mono text-xs select-all">{detail.sfTaskId}</span>
            </Fact>
          ))}
        {disclosureId && (
          <Fact label="Disclosure">
            <DisclosureLink row={row} disclosureId={disclosureId} />
          </Fact>
        )}
        {detail.disclosure && <DisclosureFacts disclosure={detail.disclosure} />}
        {detail.firmChange && <FirmChangeFacts change={detail.firmChange} />}
        {row.refreshRunId && (
          <Fact label="Refresh run" data-refresh-run={row.refreshRunId}>
            Raised by a BrokerCheck refresh run
            {can('integrations.configure') && (
              <Link
                to={refreshRunPath(row.refreshRunId)}
                className="block text-[var(--color-sapphire)] underline-offset-4 hover:underline"
              >
                Open the run
              </Link>
            )}
          </Fact>
        )}
      </dl>
      {!carried && detail.changed !== null && <WhatChanged changed={detail.changed} />}
    </PaneSection>
  );
}

/** survey-api's key of a mark carried from Salesforce's "RJ reviewed U4" (SR-410): a migrated
 *  history entry, not a Salesforce task. */
const U4_REVIEWED_KEY = 'u4-reviewed:';

/** The way to the disclosure on the participant's hub, which reveals it from the history state —
 *  the only word of it where the detail does not carry it, else a small secondary link. */
function DisclosureLink({
  row,
  disclosureId,
  secondary = false
}: {
  row: IWorkItemRow;
  disclosureId: string;
  secondary?: boolean;
}) {
  return (
    <Link
      to={personHubPath(row.participant.id)}
      state={{ reveal: disclosureAnchor(row.participant.id, disclosureId) } satisfies IRevealState}
      data-disclosure-link=""
      className={cn(
        'text-[var(--color-sapphire)] underline-offset-4 hover:underline',
        secondary ? 'w-fit text-xs' : 'text-sm'
      )}
    >
      {secondary
        ? 'Open on the record page'
        : `Open the disclosure on ${row.participant.displayName}’s page`}
    </Link>
  );
}

/** The disclosure an item is about, said as facts of "What raised this" — its type and day, the
 *  allegations whole and the resolution; the "Disclosure" section below holds the full card. */
function DisclosureFacts({ disclosure }: { disclosure: IAdminDisclosure }) {
  return (
    <>
      <Fact label="Disclosure type" data-disclosure-type="">
        {disclosure.type ?? <Missing />}
      </Fact>
      <Fact label="Disclosure date">
        {disclosure.eventDate ? formatDay(disclosure.eventDate) : <Missing />}
      </Fact>
      <Fact label="Allegations" wide data-allegations-whole="">
        {disclosure.allegations ? (
          <span className={LONG_TEXT_WHOLE}>{disclosure.allegations}</span>
        ) : (
          <Missing />
        )}
      </Fact>
      <Fact label="Resolution">{disclosure.resolution ?? <Missing />}</Fact>
    </>
  );
}

/** What a CRD or firm change moved: the firms before and after, the day, and the CRDs where they
 *  differ. */
function FirmChangeFacts({ change }: { change: IWorkItemFirmChange }) {
  const { before, after, movedOn } = change;
  const crdMoved = (before?.individualId ?? null) !== (after?.individualId ?? null);
  return (
    <>
      <Fact label="Previous firm" data-firm-before="">
        <FirmNames side={before} />
      </Fact>
      <Fact label="New firm" data-firm-after="">
        <FirmNames side={after} />
      </Fact>
      <Fact label="Moved on">{movedOn ? formatDay(movedOn) : <Missing />}</Fact>
      {crdMoved && (
        <>
          <Fact label="Previous CRD">{before?.individualId ?? <Missing />}</Fact>
          <Fact label="New CRD">{after?.individualId ?? <Missing />}</Fact>
        </>
      )}
    </>
  );
}

function FirmNames({ side }: { side: Nullable<IWorkItemFirmSide> }) {
  const names = (side?.firms ?? []).flatMap((firm) => (firm.name ? [firm.name] : []));
  return names.length === 0 ? <Missing /> : <>{names.join(', ')}</>;
}

/**
 * The disclosure the item is about (SR-396 W1, AC5) — see the module's comment: the change's kind
 * as a chip (`[data-change-kind]`, its code), the hub's card open with the moved fields marked,
 * "What changed" under it, and the hub's link last. Drawn only while the detail carries it.
 */
function DisclosureSection({ detail, row }: { detail: IWorkItemDetail; row: IWorkItemRow }) {
  const { disclosure, changeKind } = detail;
  if (!disclosure) return null;
  const moved = changedFieldsOf(detail.changed);
  return (
    <PaneSection title="Disclosure">
      {changeKind && (
        <span data-change-kind={changeKind} className="inline-flex">
          <StatusChip>{changeKindWords(changeKind)}</StatusChip>
        </span>
      )}
      <DisclosureCard
        disclosure={disclosure}
        defaultOpen
        level={3}
        changed={moved ? Object.keys(moved) : undefined}
      />
      {detail.changed !== null && <WhatChanged changed={detail.changed} />}
      <DisclosureLink row={row} disclosureId={disclosure.id} secondary />
    </PaneSection>
  );
}

/**
 * The other items the same disclosure change raised (SR-396 W4, AC8), in survey-api's order: the
 * queue, whose item it is, where it stands, its decision once reviewed, and the way to it — the
 * compliance queue on that survey, every state (`view=all`), so the list beside the pane holds it,
 * from My tasks too (the owner's Q1). None is decided from here.
 */
function Siblings({ siblings }: { siblings: IWorkItemSibling[] | undefined }) {
  if (!siblings || siblings.length === 0) return null;
  return (
    <PaneSection title="Also raised for this change">
      <ul className="grid gap-2">
        {siblings.map((sibling) => (
          <li
            key={sibling.id}
            data-sibling={sibling.id}
            className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-border px-3 py-2 text-sm"
          >
            <StatusChip>{sibling.surveyCode}</StatusChip>
            <CardLink to={personHubPath(sibling.participant.id)}>
              {sibling.participant.displayName}
            </CardLink>
            <WorkItemStateChip state={sibling.state} />
            {sibling.state === 'reviewed' && sibling.decision && (
              <StatusChip>{decisionLabel(sibling.decision)}</StatusChip>
            )}
            <Link
              to={workItemPath(
                sibling.id,
                `?survey=${encodeURIComponent(sibling.surveyCode)}&view=all`
              )}
              className="text-[var(--color-sapphire)] underline-offset-4 hover:underline"
            >
              Open the item
            </Link>
          </li>
        ))}
      </ul>
    </PaneSection>
  );
}

/**
 * What a changed disclosure changed — survey-api's stored `{member: {old, new}}`, one row a member
 * in the order stored, each labelled through `disclosure-fields.ts` by the card's own table
 * (`ChangedFields`, named "What changed" here). A document of another shape is drawn whole as JSON
 * rather than guessed at.
 */
function WhatChanged({ changed }: { changed: unknown }) {
  const fields = changedFieldsOf(changed);
  if (fields === null) {
    return (
      <pre className="overflow-x-auto rounded-sm bg-muted p-3 text-xs">
        {JSON.stringify(changed, null, 2)}
      </pre>
    );
  }
  return <ChangedFields changed={fields} label="What changed" />;
}

type ChangedMembers = NonNullable<IAdminDisclosureChange['changed']>;

/** `changed` read as the `{member: {old, new}}` it is stored as — or `null` for any other shape. */
function changedFieldsOf(changed: unknown): Nullable<ChangedMembers> {
  if (typeof changed !== 'object' || changed === null || Array.isArray(changed)) return null;
  const fields: ChangedMembers = {};
  for (const [member, value] of Object.entries(changed)) {
    if (typeof value !== 'object' || value === null || !('old' in value) || !('new' in value)) {
      return null;
    }
    fields[member] = { old: value.old, new: value.new };
  }
  return fields;
}

/** The item's events, newest first as sent: when, who, what, and what they wrote. */
function History({ events }: { events: IWorkItemEvent[] }) {
  return (
    <PaneSection title="History">
      {events.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nothing has happened to this item yet.</p>
      ) : (
        <WorkItemEvents events={events} label="History" />
      )}
    </PaneSection>
  );
}

/** An item's events as a list, newest first as sent — the pane's history, and each item's under
 *  the hub's compliance history (`label` names the list among the others there). */
export function WorkItemEvents({ events, label }: { events: IWorkItemEvent[]; label: string }) {
  return (
    <ol aria-label={label} className="grid gap-3">
      {events.map((event) => (
        <li key={event.id} data-event={event.kind} className="grid gap-0.5 text-sm">
          <span className={isKnownEvent(event.kind) ? 'font-medium' : 'font-mono'}>
            {escalatedWords(event, events) ?? eventWords(event)}
          </span>
          <span className="text-xs text-muted-foreground">
            {formatDateTime(event.at)} · {actorOf(event)}
          </span>
          {event.rationale && <p className="whitespace-pre-wrap">{event.rationale}</p>}
        </li>
      ))}
    </ol>
  );
}

/** An escalation under the hierarchy in words — "Escalated to level 2 — whom", the assignee the
 *  `assigned` event survey-api writes after it names — or null for any other event. */
function escalatedWords(event: IWorkItemEvent, events: readonly IWorkItemEvent[]): Nullable<string> {
  if (event.kind !== 'decided' || event.decision !== 'escalate' || event.escalationLevel == null) {
    return null;
  }
  // The very next event, by id: an `assigned` one there is the escalation's own.
  const next = events
    .filter((other) => other.id > event.id)
    .reduce<Nullable<IWorkItemEvent>>((first, other) => (first && first.id < other.id ? first : other), null);
  const whom = next?.kind === 'assigned' ? next.assignee?.email : undefined;
  return `Escalated to level ${event.escalationLevel}${whom ? ` — ${whom}` : ''}`;
}

/** One event in words: its kind, and what it set — the decision, the day, the assignee. */
function eventWords(event: IWorkItemEvent): string {
  const parts = [eventLabel(event.kind)];
  if (event.decision) parts.push(decisionLabel(event.decision));
  if (event.kind === 'assigned') parts.push(event.assignee ? `to ${event.assignee.email}` : 'to nobody');
  else if (event.assignee) parts.push(`assigned to ${event.assignee.email}`);
  if (event.followUpOn) parts.push(`follow up on ${formatDay(event.followUpOn)}`);
  return parts.join(' — ');
}

/** Who acted: the account's address, or the role in words where no account did. */
function actorOf(event: IWorkItemEvent): string {
  return event.actor.email ?? actorRoleLabel(event.actor.role);
}
